import clsx from "clsx";
import { MemoTable } from "../components/MemoTable";
import { JumpToSection } from "../components/JumpToSection";
import { ComingSoonSection } from "../components/ComingSoonSection";
import { parseMarkdownTable } from "@/utils/misc/parseMarkdownTable";
import { useMemo } from "react";
import { ensureHttps } from "@/utils/misc";
import { FinanceLabField } from "@/entities/FinanceLab/FinanceLab.model";
import { ColDef } from "ag-grid-community";
import { Link } from "../components/Link";
import { parseMarkdownLinks } from "@/utils";
import { useApiQuery } from "@/api";
import { getFinanceLabMetrics } from "@/entities/FinanceLab/FinanceLab.queries";
import { LabelLLM } from "../components/LabelLLM";

const CompanyNameCellRenderer = ({
    value,
    data,
}: {
    value: string;
    data: { websiteUrl?: string };
}) => {
    if (!value) return "-";
    if (data?.websiteUrl) {
        return (
            <a
                href={data.websiteUrl}
                target="_blank"
                rel="noreferrer"
                className="text-link"
            >
                {value}
            </a>
        );
    }
    return <span>{value}</span>;
};

const CellWithLinksRenderer = ({ value }: { value: string }) => {
    if (!value) return <span>-</span>;

    const parts = parseMarkdownLinks(value);
    const textParts = parts.filter((part) => typeof part === "string");
    const linkParts = parts.filter((part) => typeof part !== "string");

    return (
        <div className="flex flex-col gap-3 py-2">
            <span className="break-words">{textParts.join(" ")}</span>
            {linkParts.length > 0 && (
                <div className="flex flex-wrap gap-2">
                    {linkParts.map((part, index) => {
                        if (typeof part === "string") return null;
                        return (
                            <Link
                                key={index}
                                href={part.url}
                                title={part.text}
                            />
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export function IndustryOverview() {
    const {
        data: competitiveAnalysisMetrics,
        isLoading: isLoadingCompetitiveAnalysis,
    } = useApiQuery(getFinanceLabMetrics, {
        params: {
            labels: [FinanceLabField.ORGANIZATION_COMPETITORS_TOP_5],
        },
    });

    const competitorsData = useMemo(() => {
        const competitiveAnalysis = competitiveAnalysisMetrics?.find(
            (metric) =>
                metric.info.derived.label ===
                FinanceLabField.ORGANIZATION_COMPETITORS_TOP_5,
        )?.values;

        if (!competitiveAnalysis) {
            return [];
        }

        const parsedData = parseMarkdownTable(
            competitiveAnalysis?.[0]?.formattedValue || "",
        );

        return parsedData.map((row) => ({
            ...row,
            websiteUrl: row.websiteUrl ? ensureHttps(row.websiteUrl) : "",
        }));
    }, [competitiveAnalysisMetrics]);

    const competitorTableColumns = useMemo(() => {
        if (competitorsData.length === 0) return [];

        const firstRow = competitorsData[0];
        const keys = Object.keys(firstRow).filter(
            (key) => key !== "websiteUrl",
        );

        return keys.map((key) => {
            const headerMap: Record<string, string> = {
                companyName: "Name",
                estimatedRevenue: "Estimated Revenue",
                headcount: "Headcount",
                headquartersLocation: "HQ Location",
                naicsId: "NAICS ID",
                keyDifferences: "Key Differences",
                score: "Score",
            };

            const column: ColDef = {
                field: key,
                headerName: headerMap[key] || key,
                flex: key === "keyDifferences" ? 3 : 1,
                minWidth:
                    key === "keyDifferences"
                        ? 300
                        : key === "companyName" ||
                            key === "estimatedRevenue" ||
                            key === "headcount"
                          ? 160
                          : 140,
            };

            if (key === "companyName") {
                column.pinned = "left";
                column.cellRenderer = CompanyNameCellRenderer;
            }

            if (
                key === "estimatedRevenue" ||
                key === "headcount" ||
                key === "headquartersLocation"
            ) {
                column.cellRenderer = CellWithLinksRenderer;
                column.wrapText = true;
                column.autoHeight = true;
            }

            if (key === "score") {
                column.headerTooltip =
                    "This score is a competitor match score, which factors in a combination of offering similarity, market visibility, and market traction. A score of 5 represents the strongest possible match.";
            }

            return column;
        });
    }, [competitorsData]);

    return (
        <>
            <div className="space-y-8">
                <div>
                    <ComingSoonSection title="Top 5 Customers" />
                </div>

                <div>
                    <h3 className="heading-m mb-4">Top 5 Competitors</h3>
                    {isLoadingCompetitiveAnalysis ? (
                        <div className="flex w-full flex-col gap-4">
                            {[1, 2, 3, 4, 5].map((i) => (
                                <div
                                    key={i}
                                    className={clsx(
                                        "h-12 w-full bg-slate-700",
                                        "animate-pulse rounded",
                                    )}
                                />
                            ))}
                        </div>
                    ) : competitorsData.length > 0 ? (
                        <>
                            <div className="mb-2">
                                <MemoTable
                                    data={competitorsData}
                                    columns={competitorTableColumns}
                                />
                            </div>
                            <div className="mb-2">
                                <p className="body-micro subdued">
                                    Scores are based on weighted factors
                                    including product/offering similarity,
                                    revenue proximity, geographic relevance,
                                    search visibility across multiple sources,
                                    and publicly reported traction. Higher
                                    scores indicate a closer competitive match.
                                </p>
                            </div>
                            <LabelLLM />
                        </>
                    ) : (
                        <p className="body-s subdued">
                            No competitive analysis data available.
                        </p>
                    )}
                </div>

                <div>
                    <ComingSoonSection title="Top 5 Suppliers" />
                </div>
            </div>
            <JumpToSection
                targetSection="company-background"
                label="Jump to Company Background"
            />
        </>
    );
}
